import { useMemo, useState } from 'react';

import Checkbox from '../../../Checkbox';
import Input from '../../../Input';
import PillV2 from '../../../PillV2';

import { CARDINALITY_THRESHOLD, LIST_RENDER_CAP } from '../../utilities/constants';
import { filterOptionsByQuery } from '../../utilities/helpers';
import type { FacetOption } from '../../utilities/types';

import styles from './_index.module.scss';

export type ValueSelectListProps = {
  idPrefix: string;
  /** Cap on selected values; once reached, unselected rows are disabled (not hidden). */
  maxSelected?: number;
  onChange: (values: string[]) => void;
  options: FacetOption[];
  searchPlaceholder?: string;
  selectedValues: string[];
};

/**
 * Cardinality-adaptive value list: ≤ CARDINALITY_THRESHOLD options render as a
 * flat checkbox list with no search; above it a search box appears and values
 * are sorted by respondent count descending. Selected values render as
 * removable PillV2 chips above the list; an optional `maxSelected` cap disables
 * further selection.
 */
const ValueSelectList = ({
  idPrefix,
  maxSelected,
  onChange,
  options,
  searchPlaceholder = 'Search…',
  selectedValues,
}: ValueSelectListProps) => {
  const [query, setQuery] = useState('');

  const showSearch = options.length > CARDINALITY_THRESHOLD;
  const orderedOptions = useMemo(
    () => (showSearch ? [...options].sort((a, b) => (b.count ?? 0) - (a.count ?? 0)) : options),
    [options, showSearch]
  );
  const visible = useMemo(
    () => (showSearch ? filterOptionsByQuery(orderedOptions, query) : orderedOptions),
    [orderedOptions, query, showSearch]
  );

  const selected = new Set(selectedValues);
  const atMax = maxSelected != null && selected.size >= maxSelected;
  const labelByValue = useMemo(
    () => new Map(options.map(({ label, value }) => [value, label])),
    [options]
  );

  const toggle = (value: string) => {
    if (selected.has(value)) {
      onChange(selectedValues.filter((selectedValue) => selectedValue !== value));
    } else if (!atMax) {
      onChange([...selectedValues, value]);
    }
  };

  return (
    <div className={styles['value-select-list']} data-testid={`${idPrefix}-value-list`}>
      {selectedValues.length > 0 && (
        <ul className={styles['chips']} data-testid={`${idPrefix}-chips`}>
          {selectedValues.map((value) => {
            const label = labelByValue.get(value) ?? value;
            const removeValue = () => toggle(value);

            return (
              <li key={value}>
                <PillV2
                  aria-label={`Remove ${label}`}
                  className={styles['chip']}
                  data-testid={`${idPrefix}-chip-${value}`}
                  isModified
                  onClick={removeValue}
                  onClose={removeValue}
                  value={
                    <span className={styles['chip-label']} title={label}>
                      {label}
                    </span>
                  }
                />
              </li>
            );
          })}
        </ul>
      )}

      {showSearch && (
        <Input
          aria-label={searchPlaceholder}
          className={styles['search']}
          data-testid={`${idPrefix}-search`}
          onChange={(value) => setQuery(String(value))}
          placeholder={searchPlaceholder}
          type="search"
          value={query}
        />
      )}

      <ul className={styles['options']}>
        {visible.slice(0, LIST_RENDER_CAP).map((option) => {
          const checked = selected.has(option.value);
          return (
            <li key={option.value}>
              <Checkbox
                aria-label={option.label}
                className={styles['option-checkbox']}
                id={`${idPrefix}-${option.value}`}
                isChecked={checked}
                isDisabled={!checked && atMax}
                label={
                  // <div>, not <span> — avoids the shared Checkbox's helpText rule
                  // that absolutely-positions a trailing span:last-of-type.
                  <div className={styles['option']}>
                    <span className={styles['option-label']} title={option.label}>
                      {option.label}
                    </span>
                    {option.count != null && (
                      <span className={styles['option-count']}>
                        {`(${option.count.toLocaleString()})`}
                      </span>
                    )}
                  </div>
                }
                onChange={() => toggle(option.value)}
                value={option.value}
              />
            </li>
          );
        })}
        {visible.length > LIST_RENDER_CAP && (
          <li className={styles['more']}>
            Showing {LIST_RENDER_CAP} of {visible.length} — refine search to narrow.
          </li>
        )}
      </ul>
    </div>
  );
};

export default ValueSelectList;
